import { useState } from 'react';
import { Appointment, CancelConflictsResult, ApiError, cancelTimeOffAppointments } from '../api';
import { formatSalon } from '../lib/datetime';
import { Loader2, X, AlertTriangle, Phone } from 'lucide-react';

interface TimeOffConflictDialogProps {
  open: boolean;
  /** Bookings that fall inside the block the owner just saved. */
  conflicts: Appointment[];
  /** Human range of the saved block, e.g. "Mon 10 Aug 2026 → Thu 20 Aug 2026". */
  range: string;
  /** The block being cancelled for, recorded so lifting it can offer these back. */
  timeOffId: number;
  /** Called once the dialog closes, with true if any booking was cancelled. */
  onClose: (cancelledAny: boolean) => void;
}

/**
 * Shown after a block is saved over existing bookings.
 *
 * The block is already saved — it's a statement of fact. What's undecided is the
 * bookings. Nothing is sent to the customer (Calendyz has no appointment
 * notifications), so cancelling silently strands people until the owner calls
 * them — which is why the result screen hands back every name and number rather
 * than a tidy success count.
 *
 * Cancelling here is undoable: each booking records this block, so deleting or
 * shortening it later offers them back (see TimeOffRestoreDialog).
 *
 * The heading names the block's *date range*, not just a count, because a
 * mistyped year produces a perfectly plausible-looking list and the dates are the
 * only thing that gives it away.
 */
export default function TimeOffConflictDialog({
  open,
  conflicts,
  range,
  timeOffId,
  onClose,
}: TimeOffConflictDialogProps) {
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<CancelConflictsResult | null>(null);

  if (!open) return null;

  const close = () => {
    const cancelledAny = (result?.cancelled ?? 0) > 0;
    setError('');
    setResult(null);
    onClose(cancelledAny);
  };

  const handleCancelBookings = async () => {
    setCancelling(true);
    setError('');
    try {
      setResult(
        await cancelTimeOffAppointments({
          appointmentIds: conflicts.map((c) => c.id),
          timeOffId,
        }),
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not cancel these bookings.');
    }
    setCancelling(false);
  };

  const count = conflicts.length;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="animate-in fade-in zoom-in bg-surface flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl shadow-xl duration-200">
        <div className="border-subtle flex items-start justify-between gap-3 border-b px-6 py-4">
          <div className="flex gap-3">
            <span className="bg-warn-soft mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full">
              <AlertTriangle className="text-warn h-4 w-4" />
            </span>
            <div>
              <h3 className="text-fg text-lg leading-tight font-bold">
                {result
                  ? 'Bookings cancelled'
                  : `${count} booking${count === 1 ? '' : 's'} inside ${range}`}
              </h3>
              {!result && (
                <p className="text-fg-secondary mt-1 text-sm">
                  Your time off is saved. These bookings fall inside it. Cancelling frees the slots,
                  but the customers are <strong>not</strong> told — you'll get their numbers to
                  call.
                </p>
              )}
            </div>
          </div>
          <button
            onClick={close}
            disabled={cancelling}
            className="hover:bg-muted rounded-full p-2 transition-colors disabled:pointer-events-none disabled:opacity-40"
          >
            <X className="text-fg-subtle h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          {result ? (
            <div className="space-y-3">
              <p className="text-fg-secondary text-sm">
                <strong>{result.cancelled}</strong> booking
                {result.cancelled === 1 ? '' : 's'} cancelled.
              </p>
              {result.toContact.length > 0 && (
                <div className="border-warn/30 bg-warn-soft rounded-lg border px-3 py-3">
                  <p className="text-warn text-sm font-semibold">
                    Nobody has been told — please call{' '}
                    {result.toContact.length === 1 ? 'this customer' : 'these customers'}:
                  </p>
                  <ul className="mt-2 space-y-1">
                    {result.toContact.map((c, i) => (
                      <li key={i} className="text-warn flex items-center gap-2 text-sm">
                        <Phone className="h-3.5 w-3.5 shrink-0" />
                        <span className="font-medium">{c.name}</span>
                        <span className="text-warn">{c.phone || 'no number on file'}</span>
                        <span className="text-warn ml-auto shrink-0 text-xs">
                          {formatSalon(c.startTime, 'EEE d MMM · h:mm a')}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <>
              <ul className="divide-subtle border-default divide-y rounded-lg border">
                {conflicts.map((appointment) => (
                  <li key={appointment.id} className="px-3 py-2.5">
                    <p className="text-fg text-sm font-semibold">
                      {formatSalon(appointment.startTime, 'EEE d MMM')} ·{' '}
                      {formatSalon(appointment.startTime, 'h:mm a')}
                    </p>
                    <p className="text-fg-secondary text-xs">
                      {appointment.serviceName} — {appointment.customer.name}
                      {appointment.staffName ? ` · ${appointment.staffName}` : ''}
                    </p>
                  </li>
                ))}
              </ul>
              {error && (
                <div className="border-danger/25 bg-danger-soft text-danger mt-3 rounded-lg border px-3 py-2 text-sm">
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        <div className="border-subtle flex gap-3 border-t px-6 py-4">
          {result ? (
            <button
              onClick={close}
              className="bg-brand-600 hover:bg-brand-700 flex-1 rounded-xl px-4 py-2.5 text-sm font-bold text-white transition-colors"
            >
              Done
            </button>
          ) : (
            <>
              <button
                onClick={close}
                disabled={cancelling}
                className="border-default text-fg-secondary hover:bg-canvas flex-1 rounded-xl border px-4 py-2.5 text-sm font-semibold transition-colors disabled:pointer-events-none disabled:opacity-50"
              >
                Keep bookings
              </button>
              <button
                onClick={handleCancelBookings}
                disabled={cancelling}
                className="bg-danger-solid hover:bg-danger-solid-hover flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-60"
              >
                {cancelling && <Loader2 className="h-4 w-4 animate-spin" />}
                Cancel {count} booking{count === 1 ? '' : 's'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
